import {Component, OnDestroy, OnInit} from '@angular/core';
import {Select, Store} from '@ngxs/store';
import {AppState} from '../../state/appState';
import {Observable, Subscription} from 'rxjs';
import {MerchantModel} from '../../shared/models/merchant.model';
import {catchError, distinctUntilChanged, mergeMap} from 'rxjs/operators';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators,} from '@angular/forms';
import {Login} from '../../state/actions/merchant.actions';
import {RouteEmailMigration, RouteHome, RouteRegister} from '../../state/actions/routing.actions';
import {SetupService} from '../../shared/services/setup/setup.service';
import {MerchantStatusEnum} from '../../shared/enum/merchantStatusEnum';
import {SetupErrorCodeEnum} from '../../shared/services/setup/setupErrorCode.enum';
import {StartLoading, StopLoading} from '../../state/actions/general.actions';
import {ActivatedRoute} from '@angular/router';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  @Select(AppState.currentMerchant$)
  currentMerchant$: Observable<MerchantModel>;
  private subs: Subscription[] = [];

  public loginForm: UntypedFormGroup;
  public validCard = false;

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store,
    private setupService: SetupService,
    private activatedRoute: ActivatedRoute
  ) {
  }

  ngOnInit(): void {
    this.store.dispatch(new StartLoading());
    this.subs.push(
      this.activatedRoute.params.subscribe((p) => {
        if (p.merchantId) {
          this.initForm(p.merchantId);
          this.onCheckCard();
        } else {
          this.initForm('');
          this.store.dispatch(new StopLoading());
        }
      })
    );
    this.subs.push(
      this.currentMerchant$.pipe(distinctUntilChanged()).subscribe((p) => {
        if (p != null) {
          this.store.dispatch(new StartLoading());
          this.store.dispatch(new RouteHome());
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  initForm(merchantId: string): void {
    this.loginForm = this.fb.group({
      merchantId: new UntypedFormControl(
        {
          value: merchantId,
          disabled: this.validCard,
        },
        Validators.required
      ),
      pin: new UntypedFormControl('', Validators.required),
    });
  }

  onCheckCard(): void {
    const {merchantId} = this.loginForm.getRawValue();
    this.store
      .dispatch(new StartLoading())
      .pipe(
        mergeMap(() => this.setupService.searchCard(merchantId)),
        mergeMap((cardResponse) => {
          switch (cardResponse.status) {
            case MerchantStatusEnum.EMAIL_MIGRATION:
              return this.store.dispatch(new RouteEmailMigration(merchantId));
            case MerchantStatusEnum.INIT:
              return this.store.dispatch(new RouteRegister(merchantId));
            case MerchantStatusEnum.VALID:
              this.validCard = true;
              this.loginForm.get('merchantId').disable();
              break;
            default:
              this.loginForm.setErrors({notFound: true});
          }
          return this.store.dispatch(new StopLoading());
        }),
        catchError((error) => {
          switch (error.error.message as SetupErrorCodeEnum) {
            case SetupErrorCodeEnum.MERCHANT_NOT_FOUND_AT_PROFIPAY:
              this.loginForm.setErrors({notFound: true});
              break;
            case SetupErrorCodeEnum.USE_EMAIL_FOR_LOGIN:
              this.loginForm.setErrors({alreadyMigratedToEmail: true});
              break;
            default:
              this.loginForm.setErrors({unknownError: true});
          }
          return this.store.dispatch(new StopLoading());
        })
      )
      .subscribe();
  }

  onLoginCard(): void {
    const {merchantId, pin} = this.loginForm.getRawValue();
    this.store.dispatch(new Login(merchantId.toString().trim(), pin)).subscribe(
      () => {
      },
      () => {
        this.loginForm.get('pin').reset();
        this.loginForm.setErrors({loginFailed: true});
      }
    );
  }

  onSubmit(): void {
    this.validCard ? this.onLoginCard() : this.onCheckCard();
  }

  onResetForm(): void {
    this.validCard = false;
    this.loginForm.get('merchantId').enable();
    this.initForm('');
  }
}
